import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { Transition } from '../../components/Transition';
import { useAppDispatch, useAppSelector } from '../../store';
import { useFormik } from 'formik';
import { convertAccountType } from '../../store/accountTypeConversion/api';
import { Users } from '../../store/users/api/interfaces';
import { findUser } from '../../store/users/api';
import { DocumentType, SuperUserRole } from '../../enum';
import UploadFormModal from '../../components/UploadAssetsModal';
import useToggle from '../../hooks/useToggle';
import { useState } from 'react';
import * as Yup from 'yup';

interface IProps {
  toggle: boolean;
  onClose: () => void;
  actionUser: Users | undefined;
  search?: string | undefined;
  sortBy?: string | undefined;
  sortByOrder?: string | undefined;
}

const getValidationSchema = (targetRole: string) => {
  if (targetRole === SuperUserRole.broker) {
    return Yup.object().shape({
      targetRole: Yup.string().required('Target role is required'),
      brokerLicenseAttachmentId: Yup.string().required('Broker license is required'),
      idAttachmentId: Yup.string().required('ID attachment is required'),
      reason: Yup.string().optional(),
    });
  } else {
    return Yup.object().shape({
      targetRole: Yup.string().required('Target role is required'),
      companyName: Yup.string()
        .trim()
        .min(2, 'Company name must be at least 2 characters')
        .required('Company name is required'),
      brokerLicenseAttachmentId: Yup.string().required('Broker license is required'),
      registrationAttachmentId: Yup.string().required('Trade registration is required'),
      signatoryAttachmentId: Yup.string().required('Trade signatory is required'),
      reason: Yup.string().optional(),
    });
  }
};

const RoleConversionModal: React.FC<IProps> = ({
  toggle,
  onClose,
  actionUser,
  search,
  sortBy,
  sortByOrder,
}) => {
  const dispatch = useAppDispatch();
  const { limit, skip } = useAppSelector((state) => state.user);
  const [targetRole, setTargetRole] = useState<string>(SuperUserRole.company);
  const [brokerLicenseAttachmentId, setBrokerLicenseAttachmentId] = useState<string[]>([]);
  const [registrationAttachmentId, setRegistrationAttachmentId] = useState<string[]>([]);
  const [signatoryAttachmentId, setSignatoryAttachmentId] = useState<string[]>([]);
  const [idAttachmentId, setIdAttachmentId] = useState<string[]>([]);
  
  const {
    toggle: toggleBrokerLicense,
    onOpen: onOpenBrokerLicense,
    onClose: onCloseBrokerLicense,
  } = useToggle();
  const {
    toggle: toggleRegistration,
    onOpen: onOpenRegistration,
    onClose: onCloseRegistration,
  } = useToggle();
  const {
    toggle: toggleSignatory,
    onOpen: onOpenSignatory,
    onClose: onCloseSignatory,
  } = useToggle();
  const {
    toggle: toggleIdAttachment,
    onOpen: onOpenIdAttachment,
    onClose: onCloseIdAttachment,
  } = useToggle();

  const {
    handleSubmit,
    setFieldValue,
    resetForm,
    isSubmitting,
    errors,
    touched,
    handleChange,
    values,
  } = useFormik({
    initialValues: {
      targetRole: SuperUserRole.company,
      companyName: '',
      brokerLicenseAttachmentId: '',
      registrationAttachmentId: '',
      signatoryAttachmentId: '',
      idAttachmentId: '',
      reason: '',
    },
    validationSchema: getValidationSchema(targetRole),
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        if (!actionUser?.id) {
          return;
        }

        const formattedValues: any = {
          userId: actionUser.id,
          targetRole: targetRole,
          brokerLicenseAttachmentId: values.brokerLicenseAttachmentId,
          reason: values.reason?.trim() || undefined,
        };

        if (targetRole === SuperUserRole.company) {
          formattedValues.companyName = values.companyName.trim();
          formattedValues.registrationAttachmentId = values.registrationAttachmentId;
          formattedValues.signatoryAttachmentId = values.signatoryAttachmentId;
        } else if (targetRole === SuperUserRole.broker) {
          formattedValues.idAttachmentId = values.idAttachmentId;
        }

        await dispatch(convertAccountType(formattedValues)).unwrap();

        // Refresh the users list after successful conversion
        await dispatch(
          findUser({
            limit,
            skip,
            ...(search && { search: search }),
            ...(sortBy && { sortByName: sortBy }),
            ...(sortByOrder && { sortByOrder: sortByOrder }),
          })
        ).unwrap();

        handleClose();
      } catch (error: any) {
        // Error is already handled by toastHandler in the API
      }
    },
  });

  // Update form values when attachments are selected
  const handleBrokerLicenseUpload = (attachmentIds: string[]) => {
    setBrokerLicenseAttachmentId(attachmentIds);
    setFieldValue('brokerLicenseAttachmentId', attachmentIds[0] || '');
  };

  const handleRegistrationUpload = (attachmentIds: string[]) => {
    setRegistrationAttachmentId(attachmentIds);
    setFieldValue('registrationAttachmentId', attachmentIds[0] || '');
  };

  const handleSignatoryUpload = (attachmentIds: string[]) => {
    setSignatoryAttachmentId(attachmentIds);
    setFieldValue('signatoryAttachmentId', attachmentIds[0] || '');
  };

  const handleIdAttachmentUpload = (attachmentIds: string[]) => {
    setIdAttachmentId(attachmentIds);
    setFieldValue('idAttachmentId', attachmentIds[0] || '');
  };

  const handleTargetRoleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRole = event.target.value;
    setTargetRole(newRole);
    setFieldValue('targetRole', newRole);
    // Reset form values when switching roles
    setFieldValue('companyName', '');
    setFieldValue('registrationAttachmentId', '');
    setFieldValue('signatoryAttachmentId', '');
    setFieldValue('idAttachmentId', '');
    setRegistrationAttachmentId([]);
    setSignatoryAttachmentId([]);
    setIdAttachmentId([]);
  };

  const handleClose = () => {
    resetForm();
    setTargetRole(SuperUserRole.company);
    setBrokerLicenseAttachmentId([]);
    setRegistrationAttachmentId([]);
    setSignatoryAttachmentId([]);
    setIdAttachmentId([]);
    onClose();
  };

  return (
    <>
      <Dialog
        fullWidth
        open={toggle}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        aria-describedby='role-conversion-dialog'
        maxWidth="md"
      >
        <DialogTitle sx={{ mt: -0.5, mb: -0.5 }}>
          Convert Account Type: {actionUser?.name}
        </DialogTitle>

        <form
          onSubmit={handleSubmit}
          autoComplete='off'
        >
          <DialogContent dividers>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 3 }}
            >
              Convert this individual account to a company or broker account. All required fields must be completed.
            </Typography>

            <Grid
              container
              spacing={2}
            >
              <Grid
                item
                xs={12}
                sm={12}
              >
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend">Target Role <span style={{ color: 'red' }}>*</span></FormLabel>
                  <RadioGroup
                    row
                    value={targetRole}
                    onChange={handleTargetRoleChange}
                  >
                    <FormControlLabel
                      value={SuperUserRole.company}
                      control={<Radio />}
                      label="Company"
                    />
                    <FormControlLabel
                      value={SuperUserRole.broker}
                      control={<Radio />}
                      label="Broker"
                    />
                  </RadioGroup>
                  {Boolean(errors.targetRole && touched.targetRole) && (
                    <FormHelperText error>
                      {errors.targetRole}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {targetRole === SuperUserRole.company && (
                <Grid
                  item
                  xs={12}
                  sm={12}
                >
                  <TextField
                    fullWidth
                    label="Company Name"
                    name="companyName"
                    value={values.companyName}
                    onChange={handleChange}
                    error={Boolean(errors.companyName && touched.companyName)}
                    helperText={
                      Boolean(errors.companyName && touched.companyName) && errors.companyName
                    }
                    required
                  />
                </Grid>
              )}

              <Grid
                item
                xs={12}
                sm={12}
              >
                <Box sx={{ mb: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1 }}
                  >
                    Broker License <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={onOpenBrokerLicense}
                    size="small"
                  >
                    {brokerLicenseAttachmentId.length > 0
                      ? 'Change Broker License'
                      : 'Upload Broker License'}
                  </Button>
                  {brokerLicenseAttachmentId.length > 0 && (
                    <Typography
                      variant="caption"
                      color="success.main"
                      sx={{ ml: 1 }}
                    >
                      ✓ Uploaded
                    </Typography>
                  )}
                  {Boolean(errors.brokerLicenseAttachmentId && touched.brokerLicenseAttachmentId) && (
                    <FormHelperText error>
                      {errors.brokerLicenseAttachmentId}
                    </FormHelperText>
                  )}
                </Box>
              </Grid>

              {targetRole === SuperUserRole.company && (
                <>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                  >
                    <Box sx={{ mb: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ mb: 1 }}
                      >
                        Trade Registration <span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={onOpenRegistration}
                        size="small"
                      >
                        {registrationAttachmentId.length > 0
                          ? 'Change Trade Registration'
                          : 'Upload Trade Registration'}
                      </Button>
                      {registrationAttachmentId.length > 0 && (
                        <Typography
                          variant="caption"
                          color="success.main"
                          sx={{ ml: 1 }}
                        >
                          ✓ Uploaded
                        </Typography>
                      )}
                      {Boolean(errors.registrationAttachmentId && touched.registrationAttachmentId) && (
                        <FormHelperText error>
                          {errors.registrationAttachmentId}
                        </FormHelperText>
                      )}
                    </Box>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={12}
                  >
                    <Box sx={{ mb: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ mb: 1 }}
                      >
                        Trade Signatory <span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={onOpenSignatory}
                        size="small"
                      >
                        {signatoryAttachmentId.length > 0
                          ? 'Change Trade Signatory'
                          : 'Upload Trade Signatory'}
                      </Button>
                      {signatoryAttachmentId.length > 0 && (
                        <Typography
                          variant="caption"
                          color="success.main"
                          sx={{ ml: 1 }}
                        >
                          ✓ Uploaded
                        </Typography>
                      )}
                      {Boolean(errors.signatoryAttachmentId && touched.signatoryAttachmentId) && (
                        <FormHelperText error>
                          {errors.signatoryAttachmentId}
                        </FormHelperText>
                      )}
                    </Box>
                  </Grid>
                </>
              )}

              {targetRole === SuperUserRole.broker && (
                <>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                  >
                    <Box sx={{ mb: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ mb: 1 }}
                      >
                        ID Attachment <span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={onOpenIdAttachment}
                        size="small"
                      >
                        {idAttachmentId.length > 0
                          ? 'Change ID Attachment'
                          : 'Upload ID Attachment'}
                      </Button>
                      {idAttachmentId.length > 0 && (
                        <Typography
                          variant="caption"
                          color="success.main"
                          sx={{ ml: 1 }}
                        >
                          ✓ Uploaded
                        </Typography>
                      )}
                      {Boolean(errors.idAttachmentId && touched.idAttachmentId) && (
                        <FormHelperText error>
                          {errors.idAttachmentId}
                        </FormHelperText>
                      )}
                    </Box>
                  </Grid>
                </>
              )}

              <Grid
                item
                xs={12}
                sm={12}
              >
                <TextField
                  multiline
                  rows={3}
                  fullWidth
                  label="Reason (Optional)"
                  name="reason"
                  value={values.reason}
                  onChange={handleChange}
                  error={Boolean(errors.reason && touched.reason)}
                  helperText={
                    Boolean(errors.reason && touched.reason) && errors?.reason
                  }
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleClose}
              variant='outlined'
            >
              {'Cancel'}
            </Button>
            <LoadingButton
              type='submit'
              loading={isSubmitting}
              variant='contained'
            >
              {targetRole === SuperUserRole.broker ? 'Convert to Broker' : 'Convert to Company'}
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>

      <UploadFormModal
        toggle={toggleBrokerLicense}
        onClose={onCloseBrokerLicense}
        setAttachmentArray={handleBrokerLicenseUpload}
        module={DocumentType.BrokerLicense}
        isSingleUpload={true}
      />

      <UploadFormModal
        toggle={toggleRegistration}
        onClose={onCloseRegistration}
        setAttachmentArray={handleRegistrationUpload}
        module={DocumentType.TradeRegistration}
        isSingleUpload={true}
      />

      <UploadFormModal
        toggle={toggleSignatory}
        onClose={onCloseSignatory}
        setAttachmentArray={handleSignatoryUpload}
        module={DocumentType.TradeSignatory}
        isSingleUpload={true}
      />

      <UploadFormModal
        toggle={toggleIdAttachment}
        onClose={onCloseIdAttachment}
        setAttachmentArray={handleIdAttachmentUpload}
        module={DocumentType.IDAttachment}
        isSingleUpload={true}
      />
    </>
  );
};

export default RoleConversionModal;

